import { bitcraftSnapshot } from '../data/generated/bitcraftSnapshot';
import { dataSources, getCatalogStats, librarySections, searchLibrary } from '../data/library';
import { BITJITA_ENDPOINTS, type BitjitaEndpointKey, fetchBitjitaSummary } from '../services/bitjita';
import { spaceTimeDBBackendPlan } from '../services/spacetimedb';

function anchorForExternal(url: string, label: string, className = 'link-button'): string {
  return `<a class="${className}" href="${url}" target="_blank" rel="noreferrer">${label}</a>`;
}

function renderSearchResults(query: string): string {
  const results = searchLibrary(query);
  if (query.trim().length > 0 && results.length === 0) {
    return '<p class="empty-state">No entries found in the current TypeScript snapshot. Add it to the next manual fetch pass.</p>';
  }

  if (results.length === 0) {
    return '<p class="empty-state">Search items, resources, buildings, claims, market tools, or backend notes.</p>';
  }

  return results
    .map(
      (result) => `<article class="search-result"><span>${result.type}</span><h4>${result.title}</h4><p>${result.description}</p>${
        result.href ? anchorForExternal(result.href, 'Open source', 'text-link') : ''
      }</article>`,
    )
    .join('');
}

function renderSectionCards(): string {
  return librarySections
    .map(
      (section) => `<section class="library-section" id="${section.id}">
        <div class="section-heading">
          <p>${section.eyebrow}</p>
          <h2>${section.title}</h2>
          <span>${section.description}</span>
        </div>
        <div class="entry-grid">
          ${section.entries
            .map(
              (entry) => `<a class="entry-card" href="${entry.href}" ${entry.href.startsWith('http') ? 'target="_blank" rel="noreferrer"' : ''}>
                <small>${entry.badge ?? 'Library'}</small>
                <strong>${entry.title}</strong>
                <span>${entry.description}</span>
              </a>`,
            )
            .join('')}
        </div>
      </section>`,
    )
    .join('');
}

function renderSnapshotRecords(): string {
  return bitcraftSnapshot.records
    .slice(0, 18)
    .map(
      (record) => `<tr>
        <td>${record.name}</td>
        <td>${record.kind}</td>
        <td>${record.tier || '—'}</td>
        <td>${record.tag}</td>
      </tr>`,
    )
    .join('');
}

function renderMapSamples(): string {
  return Object.entries(bitcraftSnapshot.map)
    .map(
      ([key, value]) => `<li><strong>${key.replace(/[A-Z]/g, ' $&')}</strong><span>${value.count.toLocaleString()} records</span><em>${
        value.samples.length > 0 ? value.samples.join(' · ') : 'Geometry layer'
      }</em></li>`,
    )
    .join('');
}

export function renderAppShell(root: HTMLElement): void {
  const stats = getCatalogStats();

  root.innerHTML = `<div class="app-shell">
    <aside class="sidebar" aria-label="Library navigation">
      <a class="brand" href="#top" aria-label="Clawberto BitCraft Library home">
        <span class="brand-mark">C</span>
        <span><strong>Clawberto</strong><small>BitCraft Library</small></span>
      </a>
      <nav>
        ${librarySections.map((section) => `<a href="#${section.id}">${section.eyebrow}</a>`).join('')}
        <a href="#data-sources">Data Sources</a>
        <a href="#backend-plan">Backend Plan</a>
      </nav>
      <div class="source-pill">Updated ${new Date(bitcraftSnapshot.provenance.fetchedAt).toLocaleDateString('en-GB')}</div>
    </aside>

    <main id="top" class="content">
      <header class="hero">
        <div class="hero-copy">
          <p class="kicker">Ancient Great Library · TypeScript data atlas</p>
          <h1>Clawberto BitCraft Library</h1>
          <p class="lead">A professional, source-aware BitCraft knowledge base inspired by wiki utility, redesigned as an ancient great library with manually fetched game data from Bitjita and BitCraft Map.</p>
          <div class="hero-actions">
            ${anchorForExternal('https://bitcraftmap.com/', 'Open BitCraft Map')}
            ${anchorForExternal('https://bitjita.com/docs/api', 'View Bitjita API')}
            ${anchorForExternal('https://ael-dev3.github.io/Clawberto-Bitcraft-Library/', 'Live GitHub Pages')}
          </div>
        </div>
        <div class="hero-panel" aria-label="Catalog stats">
          <span>Catalog Index</span>
          <strong>${stats.totalRecords.toLocaleString()}</strong>
          <p>records mapped across Bitjita API and BitCraft Map exports</p>
          <dl>
            <div><dt>Bitjita</dt><dd>${stats.bitjitaRecords.toLocaleString()}</dd></div>
            <div><dt>Map</dt><dd>${stats.mapRecords.toLocaleString()}</dd></div>
            <div><dt>Endpoints</dt><dd>${stats.endpointCount}</dd></div>
          </dl>
        </div>
      </header>

      <section class="search-card" aria-labelledby="search-title">
        <div>
          <p class="kicker">Scribe Search</p>
          <h2 id="search-title">Search the current library snapshot</h2>
        </div>
        <input data-testid="library-search" id="library-search" type="search" placeholder="Search claims, abyssal, buildings, market, SpaceTimeDB..." autocomplete="off" />
        <div id="search-results" class="search-results">${renderSearchResults('')}</div>
      </section>

      ${renderSectionCards()}

      <section class="library-section manuscript" id="records">
        <div class="section-heading">
          <p>Manuscript Table</p>
          <h2>Sample records from the TypeScript snapshot</h2>
          <span>Enough data is committed for GitHub Pages to render without a backend, while scripts can refresh from source.</span>
        </div>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Kind</th><th>Tier</th><th>Tag</th></tr></thead>
            <tbody>${renderSnapshotRecords()}</tbody>
          </table>
        </div>
      </section>

      <section class="library-section cartographer" aria-label="Cartographer Desk">
        <div class="section-heading">
          <p>Cartographer Desk</p>
          <h2>BitCraft Map export layers</h2>
          <span>Claims, caves, temples, ruined cities, dungeons, grids, and watchtower geometries become data-backed library context.</span>
        </div>
        <ul class="map-list">${renderMapSamples()}</ul>
      </section>

      <section class="library-section sources" id="data-sources">
        <div class="section-heading">
          <p>Data Scriptorium</p>
          <h2>Source provenance and refresh model</h2>
          <span>${bitcraftSnapshot.provenance.notes}</span>
        </div>
        <div class="source-grid">
          ${dataSources
            .map(
              (source) => `<article><small>${source.status}</small><h3>${source.name}</h3><p>${source.role}</p>${anchorForExternal(source.url, 'Visit source', 'text-link')}</article>`,
            )
            .join('')}
        </div>
        <div class="endpoint-marquee">${BITJITA_ENDPOINTS.map((endpoint) => `<span>${endpoint.label}</span>`).join('')}</div>
        <button id="refresh-bitjita" class="link-button ghost" type="button">Refresh live Bitjita counts in browser</button>
        <div id="live-refresh" class="live-refresh" aria-live="polite">Static snapshot loaded.</div>
      </section>

      <section class="library-section backend" id="backend-plan">
        <div class="section-heading">
          <p>Backend Plan</p>
          <h2>SpaceTimeDB only when static pages are not enough</h2>
          <span>${spaceTimeDBBackendPlan.reason}</span>
        </div>
        <div class="backend-grid">
          <article><h3>Candidate tables</h3><p>${spaceTimeDBBackendPlan.candidateTables.join(', ')}</p></article>
          <article><h3>First use cases</h3><p>${spaceTimeDBBackendPlan.firstUseCases.join(', ')}</p></article>
        </div>
      </section>
    </main>
  </div>`;

  const searchInput = root.querySelector<HTMLInputElement>('#library-search');
  const searchResults = root.querySelector<HTMLElement>('#search-results');
  searchInput?.addEventListener('input', () => {
    if (searchResults) {
      searchResults.innerHTML = renderSearchResults(searchInput.value);
    }
  });

  const refreshButton = root.querySelector<HTMLButtonElement>('#refresh-bitjita');
  const refreshOutput = root.querySelector<HTMLElement>('#live-refresh');
  refreshButton?.addEventListener('click', async () => {
    refreshButton.disabled = true;
    if (refreshOutput) refreshOutput.textContent = 'Refreshing Bitjita counts from the browser...';
    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8_000);
      const liveKeys: BitjitaEndpointKey[] = ['items', 'resources', 'buildings', 'creatures'];
      const summaries = await Promise.all(liveKeys.map((key) => fetchBitjitaSummary(key, controller.signal)));
      window.clearTimeout(timeout);
      if (refreshOutput) {
        refreshOutput.textContent = summaries.map((summary) => `${summary.key}: ${summary.count.toLocaleString()}`).join(' · ');
      }
    } catch (error) {
      if (refreshOutput) refreshOutput.textContent = `Live refresh unavailable, using committed snapshot. ${error instanceof Error ? error.message : ''}`;
    } finally {
      refreshButton.disabled = false;
    }
  });
}
